import { cva } from 'class-variance-authority'

// sz:kit (A1, owner 2026-10-29) — hover is the mock's: each kind SHIFTS its own surface rather than
// fading the whole button. A press scales the button to .98 over the fast step (the mock's
// `transform: scale(.98)`), and only where motion is welcome; under reduced motion a press changes
// nothing, as the mock's would with its transitions off. Disabled sits under both: it takes no
// pointer, so neither state can reach it.
const PRESS = 'motion-safe:active:scale-98'

// Brand accent lives here, in chrome — never in a data region. Hover lifts the gold to the mock's
// `--accent-hover` (#e2b755).
const PRIMARY = `bg-primary text-primary-foreground hover:bg-button-primary-hover ${PRESS}`

// The hairline is load-bearing, not decoration: the fill is the inset grey,
// 1.02:1 on a dark card, so the edge is what draws the button. Hover lights that edge to the
// strong hairline (mock `#262626` → `#3a3a3a`); the fill stays.
const SECONDARY = `border border-button-secondary-border bg-button-secondary-bg text-secondary-foreground hover:border-border-strong ${PRESS}`

const DESTRUCTIVE = `bg-destructive-fill text-destructive-foreground hover:bg-destructive-fill-hover ${PRESS}`

// No fill and no edge: the words carry it, in the link colour, and underline on hover as the
// mock's text links do (offset 3).
const QUIET = `text-link underline-offset-3 hover:underline ${PRESS}`

// Figma 34 → h-9 (4-grid).
const MD = 'h-9 px-4'

/**
 * The below-`lg` touch target every small control wears (Button, IconButton,
 * Checkbox): a transparent `::before` at least 44px each way, centred on the
 * control, whatever size the control is drawn at. Each inset is
 * `min(0, (100% - 44px) / 2)`, so a caller's 32px icon button grows 6px a side
 * and a 16px one 14, while a labelled button, already wider than 44, grows only
 * vertically: two buttons side by side never share a target. A control 44px
 * or more each way does not grow at all.
 *
 * One `inset` declaration on purpose: a caller that still passes its own
 * `before:-inset-*` replaces it whole through `cn`, where a centring translate
 * would be left behind to push the caller's box off the control.
 * `pointer-events` stays inherited, so a control a caller makes inert (a
 * checkbox inside a clickable row) keeps an inert target.
 */
export const TOUCH_TARGET =
  "before:absolute before:inset-[min(0px,calc((100%-(--spacing(11)))/2))] before:content-[''] lg:before:hidden"

/**
 * `TOUCH_TARGET` grown vertically only, for controls that sit side by side in
 * a row — chips, segments, period tabs, the market-status pill — where a
 * sideways growth would lay one control's target over its neighbour's. The
 * same centred `min(0, (100% - 44px) / 2)`, so every height lands on exactly
 * 44px without a per-size inset rounded onto the 4-grid.
 */
export const TOUCH_TARGET_Y =
  "touch-manipulation before:absolute before:inset-x-0 before:inset-y-[min(0px,calc((100%-(--spacing(11)))/2))] before:content-[''] lg:before:hidden"

/**
 * The button skin, separate from the component so a router <Link> can wear it
 * without Button growing a polymorphic `as` prop — and so Button.tsx exports
 * only a component, which is what Fast Refresh needs.
 *
 * A link that looks like a button must still BE a link: middle-click,
 * copy-address and the browser's focus order all depend on the element.
 *
 * No type role in the base: every size names its own, and the role carries its
 * weight. A base `font-medium` is emitted after `.text-h-s`/`.text-h-m`, so it
 * held `block` and `hero` at 500 instead of their roles' 600.
 *
 * V2's system (`6144:2`): four kinds, all square, 16px of side padding (Figma
 * 14). Hover shifts the kind's own fill or edge and a press scales it to .98 (the revamp mock,
 * owner ruling A1, 2026-10-29); disabled goes to 38% with no pointer. Focus is the mock's white
 * control ring (`outline-ring-control`, A2).
 *
 * `md` and `icon` (36px) are under the 44px touch minimum, so below `lg` every
 * size wears `TOUCH_TARGET`, and so does a caller's own smaller height.
 * `relative` is what anchors it; a caller's `absolute`/`fixed` still wins
 * through `cn`.
 *
 * The deprecated names below still compile, so no call site breaks; each one
 * renders as the V2 kind or size it maps to until its family migrates it.
 */
export const buttonVariants = cva(
  [
    'relative inline-flex cursor-pointer touch-manipulation items-center justify-center gap-2 rounded-md whitespace-nowrap transition-[color,background-color,border-color,opacity,scale] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring-control disabled:pointer-events-none disabled:opacity-38',
    TOUCH_TARGET,
  ],
  {
    variants: {
      variant: {
        primary: PRIMARY,
        secondary: SECONDARY,
        destructive: DESTRUCTIVE,
        quiet: QUIET,
        /**
         * @deprecated Not a V2 kind. Figma's stray transparent outlined button
         * (`4236:113812`) maps to `secondary`, which this renders as. Use
         * `variant="secondary"`.
         */
        outline: SECONDARY,
        /**
         * @deprecated Made for the dialog Cancel, which V2 draws as the
         * bordered secondary (`4243:89064`), so it renders as `secondary`. Use
         * `variant="secondary"`.
         */
        muted: SECONDARY,
        /**
         * @deprecated Not a V2 kind. Renders as `secondary`: V2 draws the
         * stock header's Watch as one (`4236:73517`), and `quiet`'s underline
         * could never show on a glyph-only button. Use `variant="secondary"`,
         * or IconButton for a bare glyph.
         */
        ghost: SECONDARY,
      },
      size: {
        md: MD,
        // Dialog footers. Figma 46 → h-12 (4-grid).
        lg: 'h-12 px-4',
        // Onboarding's own CTAs, where one button carries a whole screen
        // (`4236:79789`).
        hero: 'h-14 px-8 text-h-m',
        // Sign in and sign up (`4236:72917`).
        block: 'h-13 w-full px-4 text-h-s',
        icon: 'h-9 w-9',
        /**
         * @deprecated V2 has no 32px button, so it renders as `md`. Use `md`,
         * the default.
         */
        sm: MD,
      },
      shape: {
        rounded: '',
        /**
         * @deprecated V2 draws every button square (`6144:4`, radius 0), so it
         * renders as the default. Delete the prop.
         */
        pill: '',
      },
    },
    // The 14px kinds set their type by variant: a filled button is 14/600 (`h-s`), the link 14/400
    // (`label-l`). `hero` and `block` keep the larger roles they name themselves.
    compoundVariants: [
      {
        variant: ['primary', 'secondary', 'destructive', 'outline', 'muted', 'ghost'],
        size: ['md', 'lg', 'icon', 'sm'],
        className: 'text-h-s',
      },
      { variant: 'quiet', size: ['md', 'lg', 'icon', 'sm'], className: 'text-label-l' },
    ],
    defaultVariants: { variant: 'primary', size: 'md', shape: 'rounded' },
  },
)
